"use client";

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["title", "variant", "nested", "children", "ariaLabelledById", "ariaDescribedById", "role"];
import React from "react";
import styled, { css } from "styled-components";
import { spacing, shadows, iconSize, textStyle } from "../../dimensions";
import { CheckmarkCircledFilledIcon, ExclamationMarkDiamondFilledIcon, InfoICircledFilledIcon } from "../../icons";
import { WarningFilledIcon } from "../../icons-with-colours";

// We use aria roles to ensure screen readers timely announce the messages as they appear
// The `role` attribute should be used only for content that has changed, or to bring the user's attention to it immediately. Check the Accessibility section in the docs for more details.

var icons = {
  info: /*#__PURE__*/React.createElement(InfoICircledFilledIcon, {
    fillColor: "info"
  }),
  success: /*#__PURE__*/React.createElement(CheckmarkCircledFilledIcon, {
    fillColor: "success"
  }),
  warning: /*#__PURE__*/React.createElement(WarningFilledIcon, {
    size: "1.5em"
  }),
  danger: /*#__PURE__*/React.createElement(ExclamationMarkDiamondFilledIcon, {
    fillColor: "danger"
  }),
  error: /*#__PURE__*/React.createElement(ExclamationMarkDiamondFilledIcon, {
    fillColor: "danger"
  })
};
var AlertBox = function AlertBox(_ref) {
  var title = _ref.title,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "info" : _ref$variant,
    _ref$nested = _ref.nested,
    nested = _ref$nested === void 0 ? false : _ref$nested,
    children = _ref.children,
    ariaLabelledById = _ref.ariaLabelledById,
    ariaDescribedById = _ref.ariaDescribedById,
    role = _ref.role,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Box, _extends({
    $variant: variant,
    $nested: nested,
    role: role
  }, rest), /*#__PURE__*/React.createElement(TitleWrapper, null, icons[variant], /*#__PURE__*/React.createElement(Title, {
    id: ariaLabelledById,
    $nested: nested
  }, title)), !nested && children && /*#__PURE__*/React.createElement(ChildContainer, {
    id: ariaDescribedById
  }, children));
};
export default AlertBox;
var maxTextWidth = "90ch";
var Box = styled.div.withConfig({
  displayName: "AlertBox__Box",
  componentId: "sc-tc59ff-0"
})(["--alert-border-color:", ";padding:", ";background-color:", ";", ""], function (_ref2) {
  var $variant = _ref2.$variant,
    theme = _ref2.theme;
  return $variant === "error" ? theme.status.danger : theme.status[$variant];
}, spacing.auditorium, function (props) {
  return props.theme.base.bg;
}, function (_ref3) {
  var $nested = _ref3.$nested;
  if ($nested) {
    return css(["border:1px solid var(--alert-border-color);border-radius:4px;"]);
  }
  return css(["border-top:8px solid var(--alert-border-color);box-shadow:", ";"], shadows.annular);
});
var TitleWrapper = styled.div.withConfig({
  displayName: "AlertBox__TitleWrapper",
  componentId: "sc-tc59ff-1"
})(["display:flex;gap:", ";"], spacing.auditorium);
var Title = styled.div.withConfig({
  displayName: "AlertBox__Title",
  componentId: "sc-tc59ff-2"
})(["align-self:center;max-width:", ";", ""], maxTextWidth, function (_ref4) {
  var $nested = _ref4.$nested;
  return $nested ? textStyle.rainier : textStyle.boising;
});
var ChildContainer = styled.div.withConfig({
  displayName: "AlertBox__ChildContainer",
  componentId: "sc-tc59ff-3"
})(["max-width:", ";margin-top:", ";padding-left:calc(", " + ", ");& > :first-child{margin-top:0;}& > :last-child{margin-bottom:0;}"], maxTextWidth, spacing.hall, iconSize.viewBox, spacing.auditorium);